/**
 * Shared pieces of the SOC 2 and ISO 42001 screens — V106-1-8 / V106-5-6
 * (design pass `v106-program-screens`, round 3).
 *
 *   Tabs        APG tabs (B1): roving tabindex, Left/Right/Home/End, focus follows
 *               selection; the selected tab keeps its mark AND the focus ring (B4).
 *   AskButton   "Ask <name>": opens a prefilled mail (the delivery) and records an
 *               `ask` entry, then reads "✓ Asked <name> · <date>" (persisted).
 *   Gate        a role-gated action: who does it + the one-click ask (H10).
 *   ResultRow   the row result after an action (H2): the result in words, Undo kept
 *               while focus is in the row (H26: after 10 s it goes only when focus
 *               leaves; a focused Undo hands focus to "Next gap →" first) and the
 *               server-ranked "Next gap · <reason> → <paraphrase>" (item 1, H21).
 *   FileField   the DS Field around a file input (a file is required where marked).
 *   ActButton   the DS Button, but an aria-disabled one never fires (a11y H4: the DS
 *               Button keeps aria-disabled buttons focusable and leaves the guard to us).
 *   focusWhenReady  focus a target that renders after a save / load (a11y H1: focus
 *               never falls to <body>); falls back after `timeout`.
 */
import { forwardRef, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button, Field } from '@aigovops/design-system';
import { recordProgramEntry } from '../../lib/program-screens-api';
import { KIND_GAP_LABEL, fmtDate } from '../../lib/program-screens-model';
import type { NextGap } from '../../lib/program-screens-api';

// ---- focus + guarded buttons ----------------------------------------------------------------

/**
 * Focus `primary()` as soon as it exists (polled every 40 ms); after `timeout` focus
 * `fallback()` instead. Used after saves that re-render the section and after deep links,
 * where the target appears only once a query settles.
 */
export function focusWhenReady(primary: () => HTMLElement | null | undefined, fallback?: () => HTMLElement | null | undefined, timeout = 2500): void {
  const start = Date.now();
  const tick = () => {
    const el = primary();
    if (el && el.isConnected) { el.focus(); return; }
    if (Date.now() - start > timeout) { fallback?.()?.focus(); return; }
    window.setTimeout(tick, 40);
  };
  window.setTimeout(tick, 0);
}

type ButtonProps = React.ComponentPropsWithoutRef<typeof Button>;
/** The DS Button; while aria-disabled the click is swallowed (no double submit, no gated action). */
export const ActButton = forwardRef<HTMLButtonElement, ButtonProps>(function ActButton({ onClick, ...rest }, ref) {
  const off = rest['aria-disabled'] === true || rest['aria-disabled'] === 'true';
  return <Button ref={ref} {...rest} onClick={(e) => { if (off) { e.preventDefault(); return; } onClick?.(e); }} />;
});

// ---- APG tabs --------------------------------------------------------------------------------

export interface TabDef { key: string; label: string; panel: ReactNode }

export function Tabs({ label, tabs, initial, idBase }: { label: string; tabs: readonly TabDef[]; initial?: string; idBase: string }) {
  const [sel, setSel] = useState(initial && tabs.some((t) => t.key === initial) ? initial : tabs[0]!.key);
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const select = (k: string, focus: boolean) => { setSel(k); if (focus) refs.current[k]?.focus(); };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.key === sel);
    const n = { ArrowRight: tabs[(i + 1) % tabs.length], ArrowLeft: tabs[(i - 1 + tabs.length) % tabs.length], Home: tabs[0], End: tabs[tabs.length - 1] }[e.key as 'Home'];
    if (n) { e.preventDefault(); select(n.key, true); }
  };
  return (
    <>
      <div className="v6-tabs" role="tablist" aria-label={label} onKeyDown={onKey}>
        {tabs.map((t) => (
          <button key={t.key} ref={(el) => { refs.current[t.key] = el; }} role="tab" type="button" id={`${idBase}-t-${t.key}`} aria-selected={sel === t.key}
            aria-controls={`${idBase}-p-${t.key}`} tabIndex={sel === t.key ? 0 : -1} onClick={() => select(t.key, true)} data-tab={t.key}>{t.label}</button>
        ))}
      </div>
      {tabs.map((t) => (
        <div key={t.key} role="tabpanel" id={`${idBase}-p-${t.key}`} aria-labelledby={`${idBase}-t-${t.key}`} hidden={sel !== t.key}>{sel === t.key ? t.panel : null}</div>
      ))}
    </>
  );
}

// ---- Ask ------------------------------------------------------------------------------------

export interface Person { user_id: string; name: string; email: string | null; role?: string }

/** "Ask <first name>" → mail + an `ask` entry; afterwards "✓ Asked <name> · <date>". */
export function AskButton({ slug, program, person, about, asked, onAsked, announce, label, compact = true }: {
  slug: string; program: string; person: Person; about: string; asked?: { at: string } | null; onAsked?: () => void; announce: (m: string) => void;
  label?: string; compact?: boolean;
}) {
  const [state, setState] = useState<{ at: string } | null>(asked ?? null);
  const [err, setErr] = useState<string | null>(null);
  const done = useRef<HTMLSpanElement>(null);
  useEffect(() => { if (asked) setState(asked); }, [asked]);
  useEffect(() => { if (state && document.activeElement === document.body) done.current?.focus(); }, [state]);
  const today = new Date().toISOString().slice(0, 10);
  if (state) {
    return <span className="v6-asked" ref={done} tabIndex={-1} data-part="asked"><span aria-hidden="true">✓</span>{` Asked ${person.name} · ${fmtDate(state.at, today)}`}</span>;
  }
  const first = person.name.split(/\s+/)[0] || person.name;
  const go = async () => {
    setErr(null);
    try {
      await recordProgramEntry(slug, program, { kind: 'ask', subject_key: person.user_id, data: { about: about.slice(0, 200) } });
      if (person.email) {
        const href = `mailto:${person.email}?subject=${encodeURIComponent(`GAC Pro: ${about}`)}&body=${encodeURIComponent(`${about}\n\n${typeof location !== 'undefined' ? location.href : ''}`)}`;
        try { window.open(href, '_self'); } catch { /* no mail client */ }
      }
      setState({ at: new Date().toISOString() });
      announce(`Asked ${person.name}`);
      onAsked?.();
      setTimeout(() => done.current?.focus(), 0);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'The ask could not be sent.');
    }
  };
  return <>
    <button type="button" className={`gp-linkbtn${compact ? ' gp-linkbtn--compact' : ''}`} onClick={() => void go()} data-part="ask">{label ?? `Ask ${first}`}</button>
    {err ? <span className="v6-err" role="alert">{err}</span> : null}
  </>;
}

/** Who does a gated action, and the one-click ask (H10). `id` describes the disabled control. */
export function Gate({ id, who, verb, ask }: { id: string; who: string; verb: string; ask: ReactNode }) {
  return <div className="v6-gate" data-part="gate"><span id={id}><b>{who}</b>{` ${verb}`}</span>{ask}</div>;
}

// ---- result row --------------------------------------------------------------------------------

export interface ResultSpec {
  /** "Evidence added · decide this point with it next". */
  text: string;
  tone?: 'ok' | 'bad' | 'wait';
  meta?: string;
  undo?: (() => Promise<void> | void) | null;
  /** D-106-19: the step the result asks for next ("Decide now"), so the row is never a dead end. */
  then?: { label: string; onClick: () => void } | null;
}

/**
 * The result after an action: the text, Undo (H26) and the server-ranked next gap
 * (null while loading; "none" when nothing is open).
 */
export function ResultRow({ spec, next, onNext, onUndone, autoFocus = true, rowRef }: {
  spec: ResultSpec;
  next: NextGap | null | 'none' | undefined;
  onNext: (g: NextGap) => void;
  onUndone: () => void;
  autoFocus?: boolean;
  /** The row the result sits in (focus inside it keeps Undo). */
  rowRef?: React.RefObject<HTMLElement | null>;
}) {
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const undoBtn = useRef<HTMLButtonElement>(null);
  const nextLink = useRef<HTMLButtonElement>(null);
  const [showUndo, setShowUndo] = useState(!!spec.undo);
  const expired = useRef(false);
  const [busy, setBusy] = useState(false);
  // Flow M (round 2): focus the result sentence; Undo is the next stop (Enter never undoes by accident).
  useEffect(() => { if (autoFocus) box.current?.focus(); }, [autoFocus]);
  useEffect(() => {
    if (!spec.undo) return;
    const host = rowRef?.current ?? box.current;
    const drop = () => {
      if (document.activeElement === undoBtn.current) nextLink.current?.focus();
      setShowUndo(false);
    };
    const t = window.setTimeout(() => { expired.current = true; if (!host?.contains(document.activeElement)) drop(); }, 10_000);
    const onOut = (e: FocusEvent) => { if (expired.current && !host?.contains(e.relatedTarget as Node | null)) drop(); };
    host?.addEventListener('focusout', onOut);
    return () => { window.clearTimeout(t); host?.removeEventListener('focusout', onOut); };
  }, [spec.undo, rowRef]);
  const tone = spec.tone ?? 'ok';
  return (
    <div className="v6-result" role="group" data-part="result" data-tone={tone} ref={box} tabIndex={-1} aria-labelledby={`${id}-t`}>
      <b id={`${id}-t`}>{tone === 'ok' ? <span aria-hidden="true">✓ </span> : tone === 'bad' ? <span aria-hidden="true">✕ </span> : null}{spec.text}</b>
      {spec.then ? <button type="button" className="gp-linkbtn gp-linkbtn--compact btn-primary" data-part="result-then" onClick={spec.then.onClick}>{spec.then.label}</button> : null}
      {showUndo && spec.undo ? (
        <button ref={undoBtn} type="button" className="gr-mutedlink" disabled={busy} data-part="undo"
          onClick={async () => { setBusy(true); try { await spec.undo!(); onUndone(); } finally { setBusy(false); } }}>Undo</button>
      ) : null}
      {/* D-106-19 (aesthetic A2): a result that asks for a next step on THIS row doesn't also point elsewhere. */}
      {spec.then ? null : next === undefined ? <span className="gr-small gr-dim">Finding the next gap…</span>
        : next === 'none' || next === null ? <span className="gr-small gr-dim">No other gaps open</span>
          : <button ref={nextLink} type="button" className="gr-textlink v6-plain" data-part="next-gap"
              onClick={() => onNext(next)}>{'Next gap · '}{next.kind === 'not' ? <span aria-hidden="true">✕ </span> : null}{`${KIND_GAP_LABEL[next.kind].replace('✕ ', '')} → ${next.statement}`}<span className="gp-sr-only">{`, ${next.label}`}</span></button>}
    </div>
  );
}

// ---- file field ----------------------------------------------------------------------------------

export function FileField({ id, label, required, hint, error, accept, disabled, describedBy, onFile }: {
  id: string; label: string; required?: boolean; hint?: string; error?: string | null; accept?: string; disabled?: boolean; describedBy?: string;
  onFile: (f: File | null) => void;
}) {
  return (
    <Field id={id} label={label} required={required} hint={hint} error={error ?? undefined} describedBy={describedBy} errorRole="none">
      <input type="file" className="gp-file" accept={accept} disabled={disabled} aria-disabled={disabled || undefined} required={required}
        onChange={(e) => onFile(e.currentTarget.files?.[0] ?? null)} />
    </Field>
  );
}
